/*
  <kg-cookie-banner>: eigenes Einwilligungsbanner. Die Zustimmung wird ueber die
  Consent-Schnittstelle von Shopify gesetzt (window.Shopify.customerPrivacy), damit
  Shopify, Pixel und Apps sie lesen. Ohne Zustimmung blockiert Shopify das Tracking
  weiterhin selbst. Das eingebaute Banner von Shopify muss im Admin aus sein.
*/

const FEATURE = [{ name: 'consent-tracking-api', version: '0.2' }];

class KgCookieBanner extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-cookie-dialog]');
    this.details = this.querySelector('[data-cookie-details]');
    this.schalter = [...this.querySelectorAll('[data-consent]')];

    this.addEventListener('click', (event) => {
      /* Ein Klick auf den Schalter darf die Gruppe nicht auf- oder zuklappen */
      if (event.target.matches('[data-consent]')) {
        event.stopPropagation();
        return;
      }
      const knopf = event.target.closest('[data-cookie-action]');
      if (!knopf) return;
      this.handeln(knopf.dataset.cookieAction);
    });

    /* Der Link in der Fusszeile oeffnet dieselbe Auswahl noch einmal */
    this.onOpen = () => this.zeigen(true);
    document.addEventListener('kg:privacy-open', this.onOpen);

    /*
      Menuepunkte koennen auf #cookie zeigen, dann gehen die Einstellungen auf.
      So laesst sich die Auswahl auch aus der Navigation heraus oeffnen.
    */
    this.onHashLink = (event) => {
      const link = event.target.closest('a[href*="#cookie"]');
      if (!link) return;
      event.preventDefault();
      this.zeigen(true);
    };
    document.addEventListener('click', this.onHashLink);
    if (window.location.hash.startsWith('#cookie')) {
      window.requestAnimationFrame(() => this.zeigen(true));
    }

    /* Sprachwechsel schickt das Formular ab, die Seite laedt danach neu */
    this.querySelector('[data-cookie-locale] select')?.addEventListener('change', (event) => {
      event.target.closest('form')?.submit();
    });

    this.starten();
  }

  disconnectedCallback() {
    document.removeEventListener('kg:privacy-open', this.onOpen);
    if (this.onHashLink) document.removeEventListener('click', this.onHashLink);
  }

  get privacy() {
    return window.Shopify?.customerPrivacy;
  }

  starten() {
    const laden = window.Shopify?.loadFeatures;
    if (!laden) return;
    laden.call(window.Shopify, FEATURE, (fehler) => {
      if (fehler) return;
      this.granular = this.privacy?.doesMerchantSupportGranularConsent?.() ?? false;
      this.toggleAttribute('data-granular', this.granular);
      /* Nur dort fragen, wo Shopify es verlangt, und nur solange nichts entschieden ist */
      if (this.hasAttribute('data-open')) return;
      if (this.privacy?.shouldShowGDPRBanner?.()) this.zeigen();
    });
  }

  get modal() {
    return this.classList.contains('cookie-banner--modal');
  }

  zeigen(mitDetails = false) {
    this.vorherigeAuswahl();
    this.hidden = false;
    this.details.hidden = !mitDetails;
    this.toggleAttribute('data-settings', mitDetails);
    window.requestAnimationFrame(() => this.setAttribute('data-open', ''));
    if (!this.modal) return;
    /*
      Scrollen darf der Besucher weiterhin, anklicken nicht: Die Abdunklung faengt
      alle Klicks ab, bis eine Entscheidung vorliegt.
    */
    this.dialog?.setAttribute('aria-modal', 'true');
    this.onKey = (event) => this.fokusHalten(event);
    document.addEventListener('keydown', this.onKey, true);
    /* Fokus auf den Dialog selbst: Vorleseprogramme lesen ihn vor, und kein Knopf traegt einen harten Rahmen */
    this.dialog?.setAttribute('tabindex', '-1');
    window.setTimeout(() => this.dialog?.focus(), 120);
  }

  verbergen() {
    this.removeAttribute('data-open');
    if (this.onKey) {
      document.removeEventListener('keydown', this.onKey, true);
      this.onKey = null;
    }
    window.setTimeout(() => {
      this.hidden = true;
    }, 250);
  }

  /* Solange der Dialog steht, bleibt die Tastatur darin */
  fokusHalten(event) {
    if (event.key !== 'Tab' || this.hidden) return;
    const ziele = [...this.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')];
    if (!ziele.length) return;
    const erstes = ziele[0];
    const letztes = ziele[ziele.length - 1];
    if (event.shiftKey && document.activeElement === erstes) {
      event.preventDefault();
      letztes.focus();
    } else if (!event.shiftKey && document.activeElement === letztes) {
      event.preventDefault();
      erstes.focus();
    }
  }

  /* Beim erneuten Oeffnen stehen die Schalter auf dem bisherigen Stand */
  vorherigeAuswahl() {
    const stand = this.privacy?.currentVisitorConsent?.();
    if (!stand) return;
    for (const schalter of this.schalter) {
      const wert = stand[schalter.dataset.consent];
      if (wert === 'yes' || wert === true) schalter.checked = true;
    }
  }

  handeln(aktion) {
    if (aktion === 'back') {
      this.details.hidden = true;
      this.removeAttribute('data-settings');
      return;
    }
    if (aktion === 'details') {
      this.details.hidden = !this.details.hidden;
      this.toggleAttribute('data-settings', !this.details.hidden);
      return;
    }
    if (aktion === 'accept') {
      this.speichern({ analytics: true, marketing: true, preferences: true, sale_of_data: true });
      return;
    }
    if (aktion === 'decline') {
      this.speichern({ analytics: false, marketing: false, preferences: false, sale_of_data: false });
      return;
    }
    if (aktion === 'save') {
      const auswahl = { analytics: false, marketing: false, preferences: false, sale_of_data: false };
      for (const schalter of this.schalter) auswahl[schalter.dataset.consent] = schalter.checked;
      /* Verkauf von Daten haengt am Marketing, getrennt abgefragt wird er nicht */
      auswahl.sale_of_data = auswahl.marketing;
      this.speichern(auswahl);
    }
  }

  speichern(auswahl) {
    const setzen = this.privacy?.setTrackingConsent;
    if (!setzen) {
      this.verbergen();
      return;
    }
    /* Ohne granulare Einwilligung kennt der Shop nur alles oder nichts */
    const werte = this.granular ? auswahl : { ...auswahl, analytics: auswahl.marketing || auswahl.analytics };
    setzen.call(this.privacy, werte, () => this.verbergen());
    /* Falls der Rueckruf ausbleibt, verschwindet das Banner trotzdem */
    window.setTimeout(() => this.verbergen(), 1200);
  }
}

customElements.define('kg-cookie-banner', KgCookieBanner);
